import React, { useState, useRef, useEffect } from "react";
import { Info, X } from "lucide-react";
import { useHistory, useLocation } from "react-router-dom";
import Tooltip from "../addentities/Tooltip.js";
import { createPortal } from "react-dom";
import WorkInProgress from "../../components/WorkInProgress.js";
import DiscardConfirmationModal from "../../components/DiscardConfirmationModal.js";
import AlertNotification from "../../components/AlertNotification";
import apiHelper from "../../apiHelper.js";

const portalRoot =
  typeof document !== "undefined"
    ? document.getElementById("portal-root")
    : null;
const OptionPortal = ({ children, style }) => {
  if (portalRoot) {
    return createPortal(
      <div
        className="options-container w-full bg-white border rounded shadow-lg"
        style={{ ...style, overflow: "auto", zIndex: 2000 }}
      >
        {children}
      </div>,
      portalRoot,
    );
  }
  return (
    <div
      className="options-container w-full bg-white border rounded shadow-lg"
      style={{ ...style, overflow: "auto", zIndex: 2000 }}
    >
      {children}
    </div>
  );
};

const isEmpty = (val) => !val || val.trim() === "";

const isInvalidPrice = (val) => {
  const num = Number(val.replace(/,/g, ""));
  return isNaN(num) || num <= 0;
};

const shortVariant = (v) =>
  String(v || "")
    .split(",")[0]
    .trim();

export default function BulkPricing() {
  const history = useHistory();
  const location = useLocation();
  const [showWip, setShowWip] = useState(false);

  // Discard modal state
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const discardRef = useRef(null);

  // Snapshot of initial data
  const initialSnapshotRef = useRef(null);

  const [basePriceErrors, setBasePriceErrors] = useState({});
  const [overrideErrors, setOverrideErrors] = useState({});

  // Discard modal
  //   const [discard_modal_open, set_discard_modal_open] = useState(false);
  //   const discard_ref = useRef(null);

  const [nextLocation, setNextLocation] = useState(null);
  const [discardSource, setDiscardSource] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  //   const handle_discard = () => set_discard_modal_open(true);
  //   useEffect(() => {
  //     function handleClickOutside(event) {
  //       if (discard_ref.current && !discard_ref.current.contains(event.target)) {
  //         set_discard_modal_open(false);
  //       }
  //     }
  //     if (discard_modal_open)
  //       document.addEventListener("mousedown", handleClickOutside);
  //     return () => document.removeEventListener("mousedown", handleClickOutside);
  //   }, [discard_modal_open]);

  const breadcrumbs = ["Listing", "Platform Pricing", "Bulk Pricing"];

  // Platform mapping rows (left unchanged for now)
  const [rows, set_rows] = useState([
    { id: 1, platform: "", product_id: "", sku_id: "", platform_open: false },
  ]);
  const row_refs = useRef({});
  const allowNavigationRef = useRef(false);
  const [changeReason, setChangeReason] = useState("");
  const [changeReasonError, setChangeReasonError] = useState("");
  const validateAllPrices = () => {
    const newBaseErrors = {};
    const newOverrideErrors = {};

    pricingRows.forEach((row) => {
      // Base price
      if (!row.basePrice || row.basePrice.trim() === "") {
        newBaseErrors[row.id] = "Base price is required";
      } else if (isInvalidPrice(row.basePrice)) {
        newBaseErrors[row.id] = "Enter a valid base price";
      }

      // Override prices
      row.erpPrices.forEach((price, idx) => {
        if (!price || price.trim() === "") {
          newOverrideErrors[`${row.id}-${idx}`] = "Price is required";
        } else if (isInvalidPrice(price)) {
          newOverrideErrors[`${row.id}-${idx}`] = "Enter a valid price";
        }
      });
    });

    // update state AFTER calculation
    setBasePriceErrors(newBaseErrors);
    setOverrideErrors(newOverrideErrors);

    // return result directly (NOT from state)
    return (
      Object.keys(newBaseErrors).length === 0 &&
      Object.keys(newOverrideErrors).length === 0
    );
  };

  const closeChangePriceModal = () => {
    setChangepriceModalOpen(false);
    setChangeReason("");
    setChangeReasonError("");
  };

  const add_row = () => {
    const newId = rows.length ? Math.max(...rows.map((r) => r.id)) + 1 : 1;
    set_rows((prev) => [
      ...prev,
      {
        id: newId,
        platform: "",
        product_id: "",
        sku_id: "",
        platform_open: false,
      },
    ]);
  };
  const delete_row = (id) =>
    set_rows((prev) => prev.filter((r) => r.id !== id));
  const update_row = (id, changes) =>
    set_rows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...changes } : r)),
    );

  // Save modal (existing)
  const save_ref2 = useRef(null);
  const [save_modal_open_2, set_save_modal_open_2] = useState(false);
  const handle_save_confirm_2 = () => set_save_modal_open_2(false);
  useEffect(() => {
    function outside(e) {
      if (save_ref2.current && !save_ref2.current.contains(e.target))
        set_save_modal_open_2(false);
    }
    if (save_modal_open_2) document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, [save_modal_open_2]);

  const [notification, setNotification] = useState(null);

  const handleNotificationClose = () => {
    setNotification(null);
  };

  // select stock dropdown state (kept)
  const select_stock_ref = useRef(null);
  const [select_stock_open, set_select_stock_open] = useState(false);
  const [select_stock_search, set_select_stock_search] = useState("");
  const [form_data, set_form_data] = useState({
    select_stock: "",
    id: "",
    status: "1",
    notes: "",
  });
  const toggle_select_stock = () => set_select_stock_open((s) => !s);

  const STOCK_OPTIONS = [
    {
      title: "Samsung S25 Plus",
      sku: "Mint, 12GB, 256GB",
      condition: "New",
      status: "Unactivated",
    },
    {
      title: "Samsung S25 Plus",
      sku: "Icyblue, 12GB, 256GB",
      condition: "New",
      status: "Unactivated",
    },
    {
      title: "Samsung S25 Plus",
      sku: "Navy, 12GB, 256GB",
      condition: "New",
      status: "Unactivated",
    },
    {
      title: "Samsung S25 Plus",
      sku: "Navy, 12GB, 256GB",
      condition: "Used",
      status: "Pristine",
    },
    {
      title: "Samsung S25 Plus",
      sku: "Navy, 12GB, 512GB",
      condition: "Used",
      status: "Pristine",
    },
    {
      title: "Samsung S25 Plus",
      sku: "Silver Shadow, 12GB, 256GB",
      condition: "New",
      status: "Unactivated",
    },
    {
      title: "Samsung S25 Plus",
      sku: "Blueblack, 12GB, 256GB",
      condition: "New",
      status: "Unactivated",
    },
    {
      title: "Apple iPhone 16",
      sku: "Starlight, 8GB, 256GB",
      condition: "New",
      status: "Activated",
    },
    {
      title: "Apple iPhone 16",
      sku: "Black, 8GB, 512GB",
      condition: "Used",
      status: "Refurbished",
    },
    {
      title: "Apple iPhone 15",
      sku: "Red, 6GB, 128GB",
      condition: "New",
      status: "Unactivated",
    },
    {
      title: "Apple iPad Air",
      sku: "Space Gray, 8GB, 256GB",
      condition: "New",
      status: "Unactivated",
    },
    {
      title: "Apple iPad Pro",
      sku: "Silver, 16GB, 1TB",
      condition: "Used",
      status: "Pristine",
    },
  ];

  useEffect(() => {
    const unblock = history.block((tx) => {
      // Allow navigation after confirm
      if (allowNavigationRef.current) {
        allowNavigationRef.current = false;
        return true;
      }

      // Open discard modal
      setDiscardSource("navigate");
      setNextLocation(tx);
      setDiscardModalOpen(true);

      return false;
    });

    return () => unblock();
  }, []);

  useEffect(() => {
    function onDocClick(e) {
      if (
        select_stock_ref.current &&
        !select_stock_ref.current.contains(e.target)
      ) {
        set_select_stock_open(false);
      }
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const handleCancelDiscard = () => {
    setDiscardModalOpen(false);
    setNextLocation(null);
    setDiscardSource(null);
  };

  const handleDiscardConfirm = () => {
    // Reset state (optional)
    if (initialSnapshotRef.current) {
      const snap = JSON.parse(initialSnapshotRef.current);
      if (snap?.pricingRows) {
        setPricingRows(snap.pricingRows);
      }
    }

    // Allow next navigation
    allowNavigationRef.current = true;
    setDiscardModalOpen(false);

    // Cancel button → Platform Pricing
    if (discardSource === "cancel") {
      history.push("/platform-pricing", {
        notification: {
          type: "warning",
          recordName: "Pricing",
          action: "discarded",
          message: "Pricing discarded",
        },
      });
      return;
    }

    // Navigation attempt → intended route
    if (discardSource === "navigate" && nextLocation) {
      history.push(nextLocation.pathname, nextLocation.state || {});
      return;
    }

    // Fallback
    history.push("/platform-pricing");
  };

  const handle_select_stock_select = (opt) => {
    set_form_data((prev) => ({ ...prev, select_stock: opt }));
    set_select_stock_open(false);
    set_select_stock_search("");
  };

  // --- Utilities (kept) ---
  const escapeRegExp = (string) =>
    String(string).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const normalizeForSearch = (s) =>
    String(s || "")
      .replace(/[\u2013\u2014–—\-|\/\\(),:;]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  const buildTokens = (query) =>
    normalizeForSearch(query).split(" ").filter(Boolean);
  const highlightForTokens = (text, tokens) => {
    if (!tokens || tokens.length === 0) return text;
    const uniqueTokens = Array.from(new Set(tokens)).sort(
      (a, b) => b.length - a.length,
    );
    const safePattern = uniqueTokens.map((t) => escapeRegExp(t)).join("|");
    const regex = new RegExp(`(${safePattern})`, "ig");
    return String(text)
      .split(regex)
      .map((part, i) => {
        if (!part) return null;
        if (regex.test(part)) {
          return (
            <span key={i} style={{ color: "#0D6EFD" }}>
              {part}
            </span>
          );
        }
        return part;
      });
  };

  const tokens = buildTokens(select_stock_search);
  const filteredOptions = tokens.length
    ? STOCK_OPTIONS.filter((opt) => {
        const hay = normalizeForSearch(
          `${opt.title} ${opt.sku} ${opt.condition} ${opt.status}`,
        );
        return tokens.every((tok) => hay.includes(tok));
      })
    : STOCK_OPTIONS;

  const showFinalBg = filteredOptions.length === 1 && tokens.length > 0;
  const PLATFORM_OPTIONS = ["Shopee SG", "Lazada SG", "Tik Tok SG"];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        select_stock_open &&
        select_stock_ref.current &&
        !select_stock_ref.current.contains(e.target)
      ) {
        set_select_stock_open(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [select_stock_open]);

  useEffect(() => {
    const handler = (e) => {
      const clickedInPortal = portalRoot && portalRoot.contains(e.target);
      set_rows((prev) =>
        prev.map((row) => {
          const el = row_refs.current[row.id]?.current;
          if (!clickedInPortal && el && !el.contains(e.target)) {
            return { ...row, platform_open: false };
          }
          return row;
        }),
      );
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // incomingSelected and pricingRows (kept)
  const incomingSelected = location?.state?.bulkItems ?? null;
  const listingId = incomingSelected?.[0]?.id;
  const selectedCurrencyId = incomingSelected?.[0]?.currency_id;
  console.log("incomingSelected:", JSON.stringify(incomingSelected, null, 2));
  const [selectedForBulk, setSelectedForBulk] = useState(() => {
    if (Array.isArray(incomingSelected) && incomingSelected.length)
      return incomingSelected;
    return [STOCK_OPTIONS[5], STOCK_OPTIONS[2], STOCK_OPTIONS[3]];
  });
  const [pricingRows, setPricingRows] = useState(() =>
    selectedForBulk.map((it, i) => ({
      id: i + 1,
      title: it.title,
      variant: it.sku,
      condition: it.condition,
      grading: it.status,
      basePrice: "178.00",
      erpPrices: ["1,113.00", "1,113.00", "1,113.00"],
    })),
  );
  useEffect(() => {
    setPricingRows(
      selectedForBulk.map((it, i) => ({
        id: i + 1,
        title: it.title,
        variant: it.sku,
        condition: it.condition,
        grading: it.status,
        basePrice: "178.00",
        erpPrices: ["1,113.00", "1,113.00", "1,113.00"],
      })),
    );
  }, [selectedForBulk]);
  const updatePricingRow = (rowId, changes) => {
    setPricingRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, ...changes } : r)),
    );
  };

  // Change Price modal and success state
  const changepriceRef = useRef(null);
  const [changepriceModalOpen, setChangepriceModalOpen] = useState(false);
  const [editSuccess, setEditSuccess] = useState(false);

  // when confirm change price, close modal and show success view
  const handleChangepriceConfirm = () => {
    setChangepriceModalOpen(false);
    // set success state -> this will replace the main block with success design
    setEditSuccess(true);
  };

  useEffect(() => {
    if (!initialSnapshotRef.current) {
      initialSnapshotRef.current = JSON.stringify({
        pricingRows,
      });
    }
  }, [pricingRows]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        changepriceRef.current &&
        !changepriceRef.current.contains(event.target)
      ) {
        setChangepriceModalOpen(false);
      }
    }
    if (changepriceModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [changepriceModalOpen]);

  // navigation handlers for the two buttons in success view
  const handleToListing = () => {
    // push to listing-pricing and include state to indicate which tab to open
    history.push("/listing-pricing", { activeTab: "listing", fromBulk: true });
  };
  const handleViewPricingHistory = () => {
    // push to listing-pricing with a different tab state (you can adapt keys to your router)
    history.push("/listing-pricing", {
      activeTab: "price-history",
      fromBulk: true,
    });
  };

const handleChangepriceReasonConfirm = async () => {
  if (!changeReason || changeReason.trim() === "") {
    setChangeReasonError("Reason is required");
    return;
  }

  const PLATFORM_MAP = {
    "Shopee SG": 1,
    "Lazada SG": 2,
    "Tik Tok SG": 3,
  };

  const listingsPayload = [];

  incomingSelected.forEach(item => {
    const pricesPayload = [];

    (item.platforms || []).forEach(p => {
      const platformId = PLATFORM_MAP[p.name];

      if (!platformId) {
        console.error("❌ Platform ID not found:", p.name);
        return;
      }

      pricesPayload.push({
        sales_platform_id: platformId,
        base_price: p.erpPrice
          ? parseFloat(p.erpPrice.replace("SGD ", ""))
          : null,
        override_price: p.platformPrice
          ? parseFloat(p.platformPrice.replace("SGD ", ""))
          : null,
      });
    });

    // ❗ skip listing if no prices
    if (pricesPayload.length > 0) {
      listingsPayload.push({
        listing_id: item.id,
        prices: pricesPayload,
      });
    }
  });

  if (listingsPayload.length === 0) {
    console.error("❌ No valid listings to save");
    return;
  }

  const payload = {
    reason: changeReason,
    listings: listingsPayload,
  };

  console.log("🚀 FINAL PAYLOAD", payload);

  try {
    const response = await apiHelper(
      "sales/pricing/bulk-pricing/",
      "POST",
      payload
    );

    if (response.ok) {
      setEditSuccess(true);
      setChangepriceModalOpen(false);
      setChangeReason("");
    } else {
      const err = await response.json();
      console.error("Save failed", err);
    }
  } catch (e) {
    console.error("Network error", e);
  }
};



  return (
    <div className="company-body main-company-body-inner new-look bulk-pricing-root">
      <div className="breadcrumb">
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          return (
            <span
              key={index}
              className={`breadcrumb-item ${crumb === "Platform Pricing" ? "clickable" : ""}`}
              onClick={() => {
                if (crumb === "Platform Pricing")
                  history.push("/platform-pricing");
              }}
              style={{
                cursor: crumb === "Platform Pricing" ? "pointer" : "default",
              }}
            >
              {crumb}
              {!isLast ? "  " : ""}
            </span>
          );
        })}
      </div>

      <div className="edit-organisation-info">
        <div className="edit-organisation-left">
          <span
            style={{
              color: "var(--Gray-900, #212529)",
              fontFamily: "Montserrat",
              fontSize: 20,
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "normal",
            }}
          >
            Bulk Pricing
          </span>
        </div>
        <div className="discard-save-organisation-right d-flex align-items-center justify-content-end flex-wrap gap-2">
          {/* Header buttons: when editSuccess is true show "Back to Edit", otherwise show Cancel + Save */}
          {!editSuccess ? (
            <>
              {/* <button className="dark-button" type='button' onClick={handle_discard}>Cancel</button> */}
              <button
                className="dark-button"
                type="button"
                onClick={() => {
                  setDiscardSource("cancel");
                  setNextLocation(null);
                  setDiscardModalOpen(true);
                }}
              >
                Cancel
              </button>
              <DiscardConfirmationModal
                isOpen={discardModalOpen}
                onCancel={handleCancelDiscard}
                onConfirm={handleDiscardConfirm}
                entityName="Bulk Pricing"
              />

              {notification && (
                <AlertNotification
                  type={notification.type}
                  recordName={notification.recordName}
                  action={notification.action}
                  message={notification.message}
                  onClose={handleNotificationClose}
                />
              )}
              {/* Work In Progress Modal */}
              <WorkInProgress
                isOpen={showWip}
                onClose={() => setShowWip(false)}
                onConfirm={() => setShowWip(false)}
              />

              <button
                className="dark-border"
                type="button"
                onClick={() => {
                  const hasBaseErrors = Object.values(basePriceErrors).some(
                    (err) => err && err.length > 0,
                  );
                  const hasOverrideErrors = Object.values(overrideErrors).some(
                    (err) => err && err.length > 0,
                  );

                  if (hasBaseErrors || hasOverrideErrors) {
                    // show error notification
                    setNotification({
                      type: "error",
                      recordName: "Bulk Pricing",
                      action: "saving",
                      message: "Error Saving",
                    });
                    return;
                  }

                  // clear any old error notification
                  setNotification(null);

                  // open reason modal
                  setChangepriceModalOpen(true);
                }}
              >
                Save
              </button>
            </>
          ) : (
            <button
              className="dark-button"
              type="button"
              onClick={() => setEditSuccess(false)}
            >
              Back to Edit
            </button>
          )}
        </div>
      </div>

      {/* Change Price Modal */}
      {changepriceModalOpen && (
        <>
          <div className="drawer-overlay" />
          <div
            className="entity-drawer modal-popup-cls"
            ref={changepriceRef}
            style={{
              maxWidth: 650,
              margin: "0 auto",
              background: "white",
              borderRadius: 12,
              zIndex: 1050,
              padding: 24,
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
              height: "auto",
              right: "auto",
              overflow: "inherit",
            }}
          >
            <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
              <button
                className="close-btn"
                onClick={() => {
                  setChangepriceModalOpen(false);
                  closeChangePriceModal();
                }}
              >
                <X size={20} />
              </button>
            </div>

            <h3
              style={{
                textAlign: "left",
                color: "#212529",
                fontSize: "20px",
                fontWeight: "600",
                lineHeight: "24px",
                fontFamily: "Montserrat",
                marginBottom: "5px",
              }}
            >
              Change Price
            </h3>
            <p
              style={{
                marginBottom: "0",
                textAlign: "left",
                color: "#6C757D",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "20px",
                fontFamily: "Montserrat",
              }}
            >
              Please provide a reason for the price change. This helps maintain
              a clear record for audit and tracking purposes.
            </p>

            <div className="drawer-content">
              <div className="row gappingcls-added">
                <div className="col-12">
                  <div className="info-inner tab-panel">
                    <div className="field-block">
                      <div
                        className="field-label"
                        style={{
                          display: "flex",
                          gap: "4px",
                          alignItems: "center",
                        }}
                      >
                        Reason{" "}
                        <span className="error-color" style={{ color: "red" }}>
                          *
                        </span>
                        {/* <Tooltip content="Choose Reason">
                          <Info size={16} className="info-icon" />
                        </Tooltip> */}
                      </div>
                      {/* <textarea
                        className="form-control w-100 common-textarea-all"
                        id="exampleFormControlTextarea1"
                        rows="3"
                        name="Reason"
                        style={{
                          borderColor: "#6c757d",
                          borderRadius: "8px",
                          resize: "none",
                          height: "120px",
                          overflowY: "auto",
                          fontFamily: '"Montserrat", sans-serif',
                          color: "#6c757d",
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: "18px",
                          boxShadow: "none",
                        }}
                      ></textarea> */}
                      <textarea
                        className="form-control w-100 common-textarea-all"
                        rows="3"
                        name="Reason"
                        value={changeReason}
                        onChange={(e) => {
                          setChangeReason(e.target.value);

                          // clear error while typing
                          if (changeReasonError) {
                            setChangeReasonError("");
                          }
                        }}
                        style={{
                          borderColor: changeReasonError
                            ? "#DC3545"
                            : "#6c757d",
                          borderRadius: "8px",
                          resize: "none",
                          height: "120px",
                          overflowY: "auto",
                          fontFamily: '"Montserrat", sans-serif',
                          color: "#212529",
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: "18px",
                          boxShadow: "none",
                        }}
                      />
                      {changeReasonError && (
                      <div
                        style={{ color: "#DC3545", fontSize: 12, marginTop: 4 }}
                      >
                        {changeReasonError}
                      </div>
                    )}
                    </div>
                    
                  </div>
                </div>
              </div>
            </div>

            <div className="drawer-footer" style={{ paddingTop: "10px" }}>
              <button
                className="btn-cancel"
                onClick={() => {
                  setChangepriceModalOpen(false);
                  closeChangePriceModal();
                }}
              >
                Cancel
              </button>
              {/* <button className="btn-save" onClick={handleChangepriceConfirm}>
                Save
              </button> */}
              <button
                className="btn-save"
                onClick={handleChangepriceReasonConfirm}
              >
                Save
              </button>
            </div>
          </div>
        </>
      )}

      <div className="employee-tabs-container">
        {save_modal_open_2 && (
          <>
            <div className="drawer-overlay" />
            <div
              className="entity-drawer modal-popup-cls"
              ref={save_ref2}
              style={{
                maxWidth: 650,
                margin: "0 auto",
                background: "white",
                borderRadius: 12,
                zIndex: 1050,
                padding: 24,
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                boxShadow: "0px 2px 10px rgba(0,0,0,0.2)",
                height: "auto",
                right: "auto",
                overflow: "inherit",
              }}
            >
              <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                <button
                  className="close-btn"
                  onClick={() => set_save_modal_open_2(false)}
                >
                  <X size={20} />
                </button>
              </div>
              <h3
                style={{
                  textAlign: "left",
                  color: "#212529",
                  fontSize: "20px",
                  fontWeight: "600",
                  lineHeight: "24px",
                  fontFamily: "Montserrat",
                  marginBottom: "5px",
                }}
              >
                The edit function implementation
              </h3>
              <div className="drawer-footer" style={{ paddingTop: "10px" }}>
                <button className="btn-save" onClick={handle_save_confirm_2}>
                  OK
                </button>
              </div>
            </div>
          </>
        )}

        <div className="tab-content" id="edit-spu-content">
          <div className="info-section">
            {/* If editSuccess is true, show the final success card (screenshot design). Otherwise show the edit pricing table. */}
            {editSuccess ? (
              <div className="info-card">
                <div className="info-content d-block entities-tab">
                  <div
                    className="success-visual"
                    style={{ textAlign: "center", marginBottom: 10 }}
                  >
                    <div
                      className="success-icon"
                      aria-hidden
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 999,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#FFEB00",
                        border: "2px solid #212529",
                      }}
                    >
                      <svg
                        width="16"
                        height="11"
                        viewBox="0 0 20 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 2L7 13L2 8"
                          stroke="black"
                          stroke-width="2.66667"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                  </div>

                  <h2
                    style={{
                      textAlign: "center",
                      margin: 0,
                      fontFamily: "Montserrat",
                      fontSize: 20,
                      lineHeight: "24px",
                      fontWeight: 600,
                      color: "#212529",
                    }}
                  >
                    Edit Success
                  </h2>

                  <p
                    style={{
                      textAlign: "center",
                      marginTop: 10,
                      marginBottom: 20,
                      color: "#6C757D",
                    }}
                  >
                    The following prices are now reflected on the respective
                    eCommerce Platforms
                  </p>

                  <div
                    className="edit-organisation-info"
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 10,
                      paddingTop: 10,
                      paddingBottom: 10,
                      flexWrap: "wrap",
                      margin: 0,
                      boxShadow: "none",
                      borderRadius: "0",
                      backgroundColor: "transparent",
                    }}
                  >
                    <button
                      className="dark-button"
                      onClick={handleViewPricingHistory}
                    >
                      View Pricing History
                    </button>
                    <button className="dark-border" onClick={handleToListing}>
                      To Listing
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="info-card new-info-overflow-auto main-company-body-inner"
                style={{ borderRadius: 0 }}
              >
                {/* <div className="info-header">
                  <h4 className="info-title">
                    Edit Pricing ({selectedForBulk.length})
                  </h4>
                </div> */}
                <div className="info-content d-block entities-tab">
                  <div>
                    <div
                      className="table-wrapper"
                      style={{ position: "relative", overflowX: "auto" }}
                    >
                      <table className="entities-table tab-table w-100">
                        <thead>
                          <tr>
                            <th style={{ width: "220px" }}>
                              <span>Stock</span>
                            </th>
                            <th style={{ width: "300px" }}>
                              <span>Base Price</span>
                            </th>
                            <th style={{ width: "120px" }}>
                              <span>Platform</span>
                            </th>
                            <th style={{ width: "300px" }}>
                              <span>Override</span>
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {/* {pricingRows.map((row, idx) => (
                            <PriceRow
                              key={row.id}
                              row={row}
                              rowIndex={idx}
                              updateRow={(changes) =>
                                updatePricingRow(row.id, changes)
                              }
                            />
                          ))} */}
                          {incomingSelected.map((item, idx) => (
                            <PriceRow
                              key={item.id}
                              item={item}
                              rowIndex={idx}
                              setBasePriceErrors={setBasePriceErrors}
                              basePriceErrors={basePriceErrors}
                              overrideErrors={overrideErrors}
                              setOverrideErrors={setOverrideErrors}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// function PriceRow({ row, rowIndex, updateRow }) {
//   const platforms = ["Shopee SG", "Lazada SG", "TikTok SG"];

//   const onBaseChange = (e) => {
//     updateRow({ basePrice: e.target.value });
//   };

//   const onErpChange = (idx, val) => {
//     const next = [...row.erpPrices];
//     next[idx] = val;
//     updateRow({ erpPrices: next });
//   };

//   return (
//     <tr style={{ background: rowIndex % 2 === 1 ? "#F8F9FB" : "transparent" }}>
//       <td style={{ verticalAlign: "middle" }}>
//         <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
//           <div
//             style={{
//               width: 44,
//               height: 44,
//               borderRadius: 8,
//               background: "#fff",
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
//             }}
//           >
//             <img
//               src="https://rukminim2.flixcart.com/image/704/844/xif0q/mobile/3/5/l/-original-imaghx9qmgqsk9s4.jpeg?q=90&crop=false"
//               alt=""
//               style={{ width: 36, height: 36, borderRadius: 6 }}
//             />
//           </div>

//           <div>
//             <div>{row.title}</div>
//             <div
//               style={{
//                 marginTop: 4,
//                 fontSize: 10,
//                 lineHeight: "12px",
//                 color: "#6C757D",
//               }}
//             >
//               {shortVariant(row.variant)}
//             </div>

//             <div style={{ marginTop: 4 }}>
//               <span
//                 style={{
//                   background: "#D1E7DD",
//                   color: "#198754",
//                   borderRadius: 999,
//                   border: "1px solid #198754",
//                   padding: "2px 8px",
//                   fontSize: 8,
//                   lineHeight: "10px",
//                   fontWeight: 600,
//                   marginRight: 5,
//                 }}
//               >
//                 New
//               </span>
//               <span
//                 style={{ fontSize: 10, lineHeight: "12px", color: "#6C757D" }}
//               >
//                 {row.grading}
//               </span>
//             </div>
//           </div>
//         </div>
//       </td>

//       <td style={{ verticalAlign: "middle" }}>
//         <div
//           style={{
//             display: "inline-flex",
//             alignItems: "center",
//             border: "1px solid #6C757D",
//             borderRadius: 8,
//             minWidth: 210,
//             overflow: "hidden",
//           }}
//         >
//           <div
//             style={{
//               color: "#6C757D",
//               fontWeight: 400,
//               fontSize: 14,
//               padding: "10px 14px",
//             }}
//           >
//             SGD
//           </div>
//           <input
//             value={row.basePrice}
//             onChange={onBaseChange}
//             className="form-control"
//             style={{
//               border: "none",
//               outline: "none",
//               boxShadow: "none",
//               minWidth: 150,
//               textAlign: "left",
//               borderLeft: "1px solid #6C757D",
//               borderRadius: 0,
//             }}
//           />
//         </div>
//       </td>

//       {/* <td style={{ verticalAlign: 'middle' }}>
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
//                     {platforms.map((p) => (
//                         <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
//                             <span style={{ color: '#212529', whiteSpace: 'nowrap', paddingBottom:'15px' }}>{p}</span>
//                         </div>
//                     ))}
//                 </div>
//             </td> */}
//       <td style={{ verticalAlign: "middle" }}>
//         <div
//           style={{
//             display: "flex",
//             flexDirection: "column",
//             gap: 10, // SAME gap as price column
//           }}
//         >
//           {platforms.map((p, i) => (
//             <div
//               key={i}
//               style={{
//                 display: "flex",
//                 alignItems: "center",
//                 height: 42, // match input height
//               }}
//             >
//               <span
//                 style={{
//                   color: "#212529",
//                   whiteSpace: "nowrap",
//                   fontSize: 12,
//                   fontWeight: 500,
//                 }}
//               >
//                 {p}
//               </span>
//             </div>
//           ))}
//         </div>
//       </td>

//       <td style={{ verticalAlign: "middle" }}>
//         <div
//           style={{
//             display: "flex",
//             flexDirection: "column",
//             gap: 10,
//             alignItems: "flex-end",
//           }}
//         >
//           {row.erpPrices.map((val, i) => (
//             <div
//               key={i}
//               style={{ display: "flex", gap: 8, alignItems: "center" }}
//             >
//               <div
//                 style={{
//                   display: "inline-flex",
//                   alignItems: "center",
//                   border: "1px solid #6C757D",
//                   borderRadius: 8,
//                   minWidth: 210,
//                   overflow: "hidden",
//                 }}
//               >
//                 <div
//                   style={{
//                     color: "#6C757D",
//                     fontWeight: 400,
//                     fontSize: 14,
//                     padding: "10px 14px",
//                   }}
//                 >
//                   SGD
//                 </div>
//                 <input
//                   value={val}
//                   onChange={(e) => onErpChange(i, e.target.value)}
//                   className="form-control"
//                   style={{
//                     border: "none",
//                     outline: "none",
//                     boxShadow: "none",
//                     minWidth: 150,
//                     textAlign: "left",
//                     borderLeft: "1px solid #6C757D",
//                     borderRadius: 0,
//                   }}
//                 />
//               </div>
//             </div>
//           ))}
//         </div>
//       </td>
//     </tr>
//   );
// }

function PriceRow({
  item,
  rowIndex,
  setBasePriceErrors,
  basePriceErrors,
  overrideErrors,
  setOverrideErrors,
}) {
  const PLATFORM_ORDER = ["Shopee SG", "Lazada SG", "Tik Tok SG"];
  const priceInputWrapper = {
    display: "inline-flex",
    alignItems: "center",
    border: "1px solid #CED4DA",
    borderRadius: 8,
    minWidth: 270,
    height: 42,
    overflow: "hidden",
    background: "#fff",
  };

  const currencyStyle = {
    padding: "0 12px",
    fontSize: 14,
    color: "#6C757D",
    borderRight: "1px solid #CED4DA",
    height: "100%",
    display: "flex",
    alignItems: "center",
    background: "#F8F9FA",
  };

  const inputStyle = {
    border: "none",
    outline: "none",
    boxShadow: "none",
    padding: "0 12px",
    fontSize: 14,
    height: "100%",
    width: "100%",
  };
  return (
    <tr style={{ background: rowIndex % 2 === 1 ? "#F8F9FB" : "transparent" }}>
      {/* STOCK */}
      <td style={{ verticalAlign: "middle" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
          >
            <img src={item.avatar} alt="" style={{ width: 36, height: 36 }} />
          </div>

          <div>
            <div>{item.name}</div>
            <div style={{ marginTop: 4, fontSize: 10, color: "#6C757D" }}>
              {item.variant}
            </div>

            <div style={{ marginTop: 4 }}>
              <span
                style={{
                  marginRight: 6,
                  border: "1px solid #FFEB00",
                  backgroundColor: "#FFEB00",
                  color: "#212529",
                  textAlign: "center",
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                  fontSize: 8,
                  borderRadius: 99,
                  padding: "2px 5px",
                }}
              >
                {item.condition}
              </span>
              <span
                style={{
                  marginRight: 6,
                  border: "1px solid #FFEB00",
                  backgroundColor: "#FFEB00",
                  color: "#212529",
                  textAlign: "center",
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                  fontSize: 8,
                  borderRadius: 99,
                  padding: "2px 5px",
                }}
              >
                {item.grading}
              </span>
            </div>
          </div>
        </div>
      </td>

      {/* BASE PRICE */}
      <td style={{ verticalAlign: "middle" }}>
        <div style={{ marginRight: "-20px" }}>
          <div
            style={{
              ...priceInputWrapper,
              border: basePriceErrors[item.id]
                ? "1px solid #DC3545"
                : "1px solid #CED4DA",
            }}
          >
            <div style={currencyStyle}>SGD</div>
            <input
              type="text"
            //   defaultValue={item.basePrice?.replace("SGD ", "")}
            defaultValue={
                item.basePrice !== null && item.basePrice !== undefined
                    ? String(item.basePrice).replace("SGD ", "")
                    : ""
                }
              className="form-control"
              style={{
                ...inputStyle,
                border: "none",
                color: basePriceErrors[item.id] ? "#DC3545" : "#212529",
              }}
              onChange={() => {
                // ✅ clear error immediately while typing
                setBasePriceErrors((prev) => ({
                  ...prev,
                  [item.id]: "",
                }));
              }}
              onBlur={(e) => {
                const val = e.target.value;

                setBasePriceErrors((prev) => ({
                  ...prev,
                  [item.id]:
                    !val || val.trim() === ""
                      ? "Empty Fields"
                      : isNaN(Number(val.replace(/,/g, ""))) || Number(val) <= 0
                        ? "Invalid Price"
                        : "",
                }));
              }}
            />
          </div>

          {basePriceErrors[item.id] && (
            <div
              style={{
                color: "#DC3545",
                fontSize: 12,
                marginTop: 4,
                fontWeight: 400,
              }}
            >
              {basePriceErrors[item.id]}
            </div>
          )}
        </div>
      </td>

      {/* PLATFORM */}
      <td style={{ verticalAlign: "middle" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {PLATFORM_ORDER.map((platformName) => (
            <div
              key={platformName}
              style={{
                height: 42,
                display: "flex",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 400 }}>
                {platformName}
              </span>
            </div>
          ))}
        </div>
      </td>

      {/* Override PRICE */}
      <td style={{ verticalAlign: "middle" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 10,
          }}
        >
          {PLATFORM_ORDER.map((platformName) => {
            const platform = item.platforms.find(
              (p) => p.name === platformName,
            );
            const errorKey = `${item.id}_${platformName}`;

            return (
              <div key={platformName} style={{ width: "100%" }}>
                {/* Input wrapper */}
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    border: overrideErrors[errorKey]
                      ? "1px solid #DC3545"
                      : "1px solid #CED4DA",
                    borderRadius: 8,
                    minWidth: 290,
                    height: 42,
                    overflow: "hidden",
                    background: "#fff",
                  }}
                >
                  {/* Currency */}
                  <div
                    style={{
                      padding: "0 12px",
                      fontSize: 14,
                      color: "#6C757D",
                      background: "#F8F9FA",
                      borderRight: "1px solid #CED4DA",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    SGD
                  </div>

                  {/* Input */}
                  <input
                    type="text"
                    defaultValue={
                      platform?.platformPrice?.replace("SGD ", "") || ""
                    }
                    className="form-control"
                    style={{
                      border: "none",
                      outline: "none",
                      boxShadow: "none",
                      height: "100%",
                      padding: "0 12px",
                      fontSize: 14,
                      color: overrideErrors[errorKey] ? "#DC3545" : "#212529",
                    }}
                    onChange={() => {
                      // ✅ clear error while typing
                      setOverrideErrors((prev) => ({
                        ...prev,
                        [errorKey]: "",
                      }));
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;

                      setOverrideErrors((prev) => ({
                        ...prev,
                        [errorKey]:
                          !val || val.trim() === ""
                            ? "Empty Fields"
                            : isNaN(Number(val.replace(/,/g, ""))) ||
                                Number(val) <= 0
                              ? "Invalid Price"
                              : "",
                      }));
                    }}
                  />
                </div>

                {/* Error message — scoped correctly */}
                {overrideErrors[errorKey] && (
                  <div
                    style={{
                      color: "#DC3545",
                      fontSize: 12,
                      marginTop: 4,
                      marginLeft: 4,
                      fontWeight: 400,
                    }}
                  >
                    {overrideErrors[errorKey]}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </td>
    </tr>
  );
}
